import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import * as R from 'ramda';

import { GET_APPOINTMENTS } from '@atom/graph/appointments';
import { useWorkOrdersFilters } from '@atom/hooks/useWorkOrdersFilters';
import {
  WorkOrdersConnection,
  WorkOrdersConnectionInput,
} from '@atom/types/work';

import { calculateGaps } from './weekViewUtils';

const PER_PAGE = 50;

export const useWeekViewCalendarDay = (dayOfWeek: Date) => {
  const [workOrderDay, setWorkOrderDay] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { workOrdersInput, input } = useWorkOrdersFilters();

  const onCompleted = dayRes => {
    const newOrders = R.pathOr([], ['appointments', 'appointments'], dayRes);
    const totalRecords = R.pathOr(0, ['appointments', 'totalCount'], dayRes);

    const updateTotalPages = Math.ceil(totalRecords / PER_PAGE);
    setTotalPages(updateTotalPages);

    // Store allDay orders to place at top of day
    const allDayOrders = [
      ...(workOrderDay ? workOrderDay : []),
      ...newOrders,
    ].filter(({ plannedStartDate }) => R.isNil(plannedStartDate));

    // remove existing gaps before re-calculating with next page
    const currentNoGaps = workOrderDay?.filter(order => !order?.isGap) || [];

    const day = [
      ...allDayOrders,
      ...calculateGaps([...currentNoGaps, ...newOrders]),
    ];

    setWorkOrderDay(day);
  };

  const [getDayQuery, { loading }] = useLazyQuery<
    { workOrders: WorkOrdersConnection },
    { input: WorkOrdersConnectionInput }
  >(GET_APPOINTMENTS, {
    fetchPolicy: 'no-cache',
    onCompleted,
  });

  const [dueDateStart, dueDateEnd] = useMemo(() => {
    return [startOfDay(dayOfWeek).valueOf(), endOfDay(dayOfWeek).valueOf()];
  }, [dayOfWeek]);

  const getWorkOrderInput = (
    newPage: number = 1,
  ): {
    variables: { input: WorkOrdersConnectionInput };
  } => ({
    variables: {
      input: {
        ...workOrdersInput,
        dueDateStart,
        dueDateEnd,
        page: newPage,
        limit: PER_PAGE,
        appointmentSort: true,
      },
    },
  });

  const dayWithinFilterRange = useMemo(() => {
    return (
      dueDateStart >= workOrdersInput.dueDateStart &&
      dueDateEnd <= workOrdersInput.dueDateEnd
    );
  }, [workOrdersInput]);

  const getInitDayOrders = () => {
    setWorkOrderDay(null);
    setPage(1);
    if (!workOrdersInput.withoutDueDate && dayWithinFilterRange) {
      getDayQuery(getWorkOrderInput(1));
    } else {
      setWorkOrderDay([]);
    }
  };

  useEffect(() => getInitDayOrders(), []);

  // Refresh calender when user closes preview
  useEffect(() => {
    if (R.isNil(input.workOrderId)) {
      getInitDayOrders();
    }
  }, [input.workOrderId]);

  const paginateDay = () => {
    const newPage = page + 1;
    if (totalPages >= newPage && !loading) {
      setPage(newPage);
      getDayQuery(getWorkOrderInput(newPage));
    }
  };

  return {
    workOrderDay,
    paginateDay,
  };
};
